import moment from 'moment-timezone';
import { DynamicDataObject } from '@/types/shared.types';
import { auth0 } from '@/main';
import OrderName from '@/components/DataGrid/Templates/OrderName.vue';
import { mapSaleLineToPricingInfo } from '@/utils/pricingDisplay';
import type { LinePricingInfoData } from '@/types/pricing';

export const dateTimeFormat = (date: moment.MomentInput, format = 'ddd, DD MMM hh:mm A'): string => {
  const formattedDate = moment(date).format(format);
  return formattedDate;
};

export function convertCamelCaseToTitleCase(str: string): string {
  const result = str.replace(/([A-Z])/g, ' $1');
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
}

export const convertEnumToText = (enumValue: string): string => {
  // Split the enum value by underscores and capitalize each word
  return enumValue
    ?.split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const needsRefresh = (status: string): boolean => {
  return status !== 'Posted';
};

export const sortObjectsByProperty = (array: Array<any> = [], sortBy: string, order = 'ASC') => {
  if (order == 'ASC') {
    return array?.sort((a, b) => (a[sortBy] > b[sortBy] ? 1 : -1));
  } else if (order == 'DESC') {
    return array?.sort((a, b) => (a[sortBy] > b[sortBy] ? -1 : 1));
  } else {
    return array;
  }
};

export const getLoggedInUserEmail = (): string => {
  let email = '';

  if (auth0?.user?.value?.email) {
    email = auth0.user.value.email;
  }

  return email;
};
/* =====================================================================================================================
 * ============================================ Api Client Specific Helpers ============================================
 * =====================================================================================================================
 */

export function isRequestToTheServer(data: object | any) {
  const responseKeys = Object.keys(data);
  const hasOnlyTwoKeys = responseKeys.length === 2;
  const hasSuccessAndDataKeys = responseKeys.includes('success') && responseKeys.includes('data');
  const isToServer = hasOnlyTwoKeys && hasSuccessAndDataKeys;
  return isToServer;
}

/* =====================================================================================================================
 * =========================================== Data Formatters for Data Grid ===========================================
 * =====================================================================================================================
 */

import CreateDate from '@/components/DataGrid/Templates/CreateDate.vue';
import PricingStatus from '@/components/DataGrid/Templates/PricingStatus.vue';
import PriceHealthCell from '@/components/DataGrid/Templates/PriceHealth.vue';
import { comparePriceHealth, priceHealthSortValue, type PriceHealth } from '@/utils/priceHealth';
import { updateSaleOrder } from '@/services/salesOrder.service';
import { dispatchToast } from '@/utils/dispatchToast';
import store from '@/store/store';
import { computed } from 'vue';

// Note: an empty dataset still returns the (static) columnsDefs with empty
// rowsData, so the RFQs view can render the grid headers plus an empty-state
// overlay instead of a headerless placeholder.
export const formatSaleOrdersForDataGrid = (records: DynamicDataObject[]) => {
  const saleOrderStates = computed(() => store.getters['saleOrderStates']);

  const columnsToShow = ['orderName', 'customer', 'createDate', 'csm', 'pricingStatus', 'health'];

  const formattedTable: FormattedSaleOrdersDataForDataGrid = {
    columnsDefs: [],
    rowsData: [],
  };

  const columnsDefs: ColDefForDataGrid[] = [];
  columnsToShow.forEach((colName: string) => {
    const isCsm = colName === 'csm';
    const currentColDef: ColDefForDataGrid = {
      headerName: isCsm ? colName.toUpperCase() : convertCamelCaseToTitleCase(colName),
      field: colName,
    };

    switch (colName) {
      case 'orderName':
        currentColDef['minWidth'] = 140;
        currentColDef['cellRenderer'] = OrderName;
        break;
      case 'customer':
        currentColDef['minWidth'] = 225;
        break;
      case 'createDate':
        currentColDef['minWidth'] = 160;
        currentColDef['sortingOrder'] = ['desc', 'asc', null];
        currentColDef['cellRenderer'] = CreateDate;
        // Quick filter matches raw values; expose the same format the cell
        // renders so searching "July 30" works.
        currentColDef['getQuickFilterText'] = (params: DynamicDataObject) =>
          dateTimeFormat(params.value, 'MMMM DD, YYYY');
        break;
      case 'csm':
        currentColDef['minWidth'] = 160;
        break;
      case 'pricingStatus':
        currentColDef['minWidth'] = 160;
        currentColDef['editable'] = true;
        currentColDef['cellRenderer'] = PricingStatus;
        break;
      case 'health':
        // Narrow and capped: it is an arrow and a percentage, and the flex: 1
        // default would otherwise hand it as much room as the customer name.
        // The floor is the worst case the cell can hold: "179.5%" is 55.1px of
        // tabular figures, the arrow beside it 16px, and AG Grid spends another
        // 36px on cell padding and its wrapper. 112 leaves that about 4px.
        currentColDef['minWidth'] = 110;
        currentColDef['maxWidth'] = 112;
        currentColDef['cellRenderer'] = PriceHealthCell;
        // Named so the phone layout can claw back AG Grid's 17px cell padding:
        // the column is one arrow wide there and the padding alone outgrows it.
        currentColDef['cellClass'] = 'price-health-cell';
        currentColDef['headerClass'] = 'price-health-header';
        // Worst drift first: the reason to sort this column is to find the
        // orders quoted furthest under our own number.
        currentColDef['sortingOrder'] = ['asc', 'desc', null];
        currentColDef['comparator'] = comparePriceHealth;
        // Out of the quick search: it is a derived number, and leaving it in
        // means typing "4" matches every order whose drift happens to contain a 4.
        currentColDef['getQuickFilterText'] = () => '';
        // No column filter either, for the same reason: AG Grid gives a number
        // column a "contains" text filter, so filtering on 4 would again match
        // -14.2% and 4.1% alike. Sorting is how this column is searched.
        currentColDef['filter'] = false;
        break;
    }

    columnsDefs.push(currentColDef);
  });

  // Format Rows
  const rowsData: SaleOrderRowForDataGrid[] = [];

  records.forEach((record: DynamicDataObject) => {
    const updatedRecord: SaleOrderRowForDataGrid = {
      id: record?.id,
      orderId: record?.erpId,
      orderName: record?.name,
      customer: record?.partner?.name || 'N/A',
      createDate: record?.orderDate,
      csm: record?.partner?.csm,
      pricingStatus: convertEnumToText(record?.pricingStatus),
      pricedByPriceList: record?.pricedByPriceList,
      // The column sorts on the percentage; the renderer reads the raw block
      // beside it for the detail panel.
      health: priceHealthSortValue(record?.priceHealth),
      priceHealth: record?.priceHealth ?? null,
      // Odoo's own state, shown as the last row of the Health popover. The
      // drift says nothing about where the order stands; this does.
      erpPricingStatus: record?.erpPricingStatus ?? null,
    };

    rowsData.push(updatedRecord);
  });

  formattedTable['columnsDefs'] = columnsDefs;
  formattedTable['rowsData'] = rowsData;

  return formattedTable;
};
export function createSaleLineDetails(saleLine: DynamicDataObject): SaleLineDetailsForDataTable {
  const totalCableCost: string | null = canCalculateCableCost(saleLine.cableCost, saleLine.quotedProductLength)
    ? getCableCost(saleLine.cableCost, saleLine.quotedProductLength)
    : null;

  const materialMargin: number | null = canCalculateMargins(
    saleLine.suggestedPrice ?? saleLine.enginePrice,
    totalCableCost,
    saleLine.laborCost,
    saleLine.otherCosts,
  )
    ? getMaterialMargin(
        saleLine.suggestedPrice ?? saleLine.enginePrice,
        totalCableCost ? Number(totalCableCost) : 0,
        saleLine.otherCosts,
      )
    : null;

  const productMargin = canCalculateMargins(
    saleLine.suggestedPrice ?? saleLine.enginePrice,
    totalCableCost,
    saleLine.laborCost,
    saleLine.otherCosts,
  )
    ? getProductMargin(
        saleLine.suggestedPrice ?? saleLine.enginePrice,
        totalCableCost ? Number(totalCableCost) : 0,
        saleLine.otherCosts,
        saleLine.laborCost,
      )
    : null;

  const totalCost = getTotalCost(saleLine.cableCost, saleLine.quotedProductLength, saleLine.laborCost, saleLine.otherCosts);

  const updatedRecord: SaleLineDetailsForDataTable = {
    partNumber: saleLine?.customerDefaultCode ?? '',
    partNumberBase: saleLine?.extraInfo.basePartNumber ?? '',
    familyCode: saleLine?.extraInfo.familyCode ?? '',
    uomQty: (saleLine?.productUomQty ?? '') + (saleLine?.productUom && saleLine?.productUom !== 'Each' ? ` (${saleLine?.productUom})` : ''),
    pricingType: saleLine?.pricingType ?? '',
    enginePriceType: saleLine?.priceType ?? '',
    totalCableCost: totalCableCost,
    otherCosts: saleLine?.otherCosts ?? '',
    labor: saleLine?.laborCost ?? '',
    perFootAdder: saleLine?.perFootAdder ?? '',
    suggestedPrice: saleLine?.suggestedPrice ?? '',
    enginePrice: saleLine?.enginePrice ?? '',
    odooPrice: !saleLine?.unitPrice || saleLine.unitPrice === 1 ? '' : saleLine.unitPrice,
    isPriced: saleLine?.isPriced ?? false,
    notes: saleLine?.notes ?? '',
    customerId: saleLine?.partner?.id ?? '',
    id: saleLine?.id ?? '',
    materialMargin: materialMargin,
    productMargin: productMargin,
    odooCost: saleLine?.erpCost === null ? 'N/A' : `$${saleLine.erpCost.toFixed(2)}`,
    totalCost: totalCost,
    enginePriceTooltip: null,
    erpId: saleLine?.erpId ?? '',
    isHigherLengthThanEverSold: saleLine?.extraInfo.isHigherLengthThanEverSold ?? false,
    // Surfaced so the suggested-price editor can compute a base+adder formula
    // price at this line's length, convert per-foot adders to per-meter, and
    // prepopulate the formula fields when the line already carries base+adder.
    quotedProductLength: saleLine?.quotedProductLength ?? null,
    quotedProductLengthUnits: saleLine?.quotedProductLengthUnits ?? null,
    basePrice: saleLine?.basePrice ?? null,
    perMeterAdder: saleLine?.perMeterAdder ?? null,
    priceListId: saleLine?.priceListId ?? null,
    priceListName: saleLine?.priceList?.name ?? null,
    multiplePriceListIds: saleLine?.multiplePriceListIds ?? [],
    multiplePriceListNames: saleLine?.multiplePriceLists?.map((pl: { priceList: { name: string } }) => pl.priceList?.name) ?? [],
    multiplePriceLists: saleLine?.multiplePriceLists?.map((pl: { priceList: { id: string; name: string }; price: number }) => ({
      priceListId: pl.priceList?.id,
      priceListName: pl.priceList?.name,
      price: pl.price,
    })) ?? [],
    // Structured pricing data for the shared LinePricingInfo component (same
    // mapping the search views use). The legacy enginePriceTooltip/enginePriceType
    // fields below are kept for the multiple-pricelist workflow states.
    pricingInfo: mapSaleLineToPricingInfo(saleLine),
  };

  // Handle multiple pricelists cases (check these first as they take priority)
  const hasPrice = saleLine?.suggestedPrice || saleLine?.enginePrice;

  if (saleLine?.priceListId && saleLine?.multiplePriceListIds?.length > 0 && hasPrice) {
    // User has already chosen from multiple pricelists AND has a price to display
    updatedRecord.enginePriceType = 'multiple_pricelist_selected';
    const otherPricelists = updatedRecord.multiplePriceListNames.filter(
      (name: string) => name !== updatedRecord.priceListName
    );
    const baseTooltip = `Selected from ${updatedRecord.priceListName} price list.`;
    if (otherPricelists.length > 0) {
      updatedRecord.enginePriceTooltip = `${baseTooltip}<br>(Also available in: ${otherPricelists.join(', ')})`;
    } else {
      // No names available for other pricelists, show count instead
      const otherCount = saleLine.multiplePriceListIds.length - 1;
      if (otherCount > 0) {
        updatedRecord.enginePriceTooltip = `${baseTooltip}<br>(Also available in ${otherCount} other price list${otherCount > 1 ? 's' : ''})`;
      } else {
        updatedRecord.enginePriceTooltip = baseTooltip;
      }
    }
  } else if (!saleLine?.priceListId && saleLine?.multiplePriceListIds?.length > 0 && !saleLine?.suggestedPrice ) {
    // Has multiple pricelists but hasn't selected one yet - user needs to choose
    updatedRecord.enginePriceType = 'multiple_pricelist_pending';
    const pricelistNames = updatedRecord.multiplePriceListNames.join(', ');
    if (pricelistNames) {
      updatedRecord.enginePriceTooltip = `This item appears in ${saleLine.multiplePriceListIds.length} price lists. Please select one: ${pricelistNames}`;
    } else {
      updatedRecord.enginePriceTooltip = `This item appears in ${saleLine.multiplePriceListIds.length} price lists. Please select one.`;
    }
  } else if (saleLine?.reference1 && !saleLine?.reference2) {
    const dateObject = new Date(saleLine.reference1.saleOrder.orderDate);
    const formattedDate = dateObject.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const orderStatus = translateOdooSalesOrderStatus(saleLine.reference1.saleOrder.erpPricingStatus);
    updatedRecord.enginePriceTooltip = `Based on ${saleLine.reference1.saleOrder.name}, ${formattedDate} (${orderStatus})`;
  } else if (saleLine?.reference1 && saleLine?.reference2) {
    const dateObject1 = new Date(saleLine.reference1.saleOrder.orderDate);
    const formattedDate1 = dateObject1.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const dateObject2 = new Date(saleLine.reference2.saleOrder.orderDate);
    const formattedDate2 = dateObject2.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const orderStatus1 = translateOdooSalesOrderStatus(saleLine.reference1.saleOrder.erpPricingStatus);
    const orderStatus2 = translateOdooSalesOrderStatus(saleLine.reference2.saleOrder.erpPricingStatus);
    updatedRecord.enginePriceTooltip = `Based on: </br>
    <ul>
    <li>· ${saleLine.reference1.customerDefaultCode} in ${saleLine.reference1.saleOrder.name}, ${formattedDate1} (${orderStatus1})</li>
    <li>· ${saleLine.reference2.customerDefaultCode} in ${saleLine.reference2.saleOrder.name}, ${formattedDate2} (${orderStatus2})</li>
    </ul>`;
  } else if (saleLine?.priceListId) {
    updatedRecord.enginePriceTooltip = `Based on ${saleLine.priceList.name} price list.`;
  }

  return updatedRecord;
}

function isNumeric(val: number | string | null | undefined): boolean {
  return val !== null && val !== undefined && !isNaN(parseFloat(val + '')) && isFinite(parseFloat(val + ''));
}

function hasNumericValue(val: number | string | null | undefined): boolean {
  return val !== null && val !== undefined && isNumeric(val);
}

export const canCalculateCableCost = (
  cableCost: number | string | null | undefined,
  quotedProductLength: number | string | null | undefined,
): boolean => hasNumericValue(cableCost) && hasNumericValue(quotedProductLength);

export const canCalculateMargins = (
  suggestedPrice: number | string | null | undefined,
  totalCableCost: number | string | null | undefined,
  laborCost: number | string | null | undefined,
  otherCosts: number | string | null | undefined,
): boolean =>
  hasNumericValue(suggestedPrice) &&
  (hasNumericValue(totalCableCost) || hasNumericValue(laborCost) || hasNumericValue(otherCosts));

export const getProductMargin = (
  suggestedPrice: number,
  totalCableCost: number,
  otherCosts: number,
  labor: number,
): number => {
  const difference = suggestedPrice - totalCableCost - otherCosts - labor;
  return Number(((difference / suggestedPrice) * 100).toFixed(2)); // margin in %
};

export const getMaterialMargin = (suggestedPrice: number, totalCableCost: number, otherCosts: number): number => {
  const difference = suggestedPrice - totalCableCost - otherCosts;
  return Number(((difference / suggestedPrice) * 100).toFixed(2)); // margin in %
};

export const getCableCost = (cableCost: number, quotedProductLength: number): string => {
  return (cableCost * quotedProductLength).toFixed(2);
};

export const getTotalCost = (
  cableCost: number | string | null | undefined,
  quotedProductLength: number | string | null | undefined,
  laborCost: number | string | null | undefined,
  otherCosts: number | string | null | undefined,
): number | null => {
  const cableCostValue = canCalculateCableCost(cableCost, quotedProductLength)
    ? Number(getCableCost(Number(cableCost), Number(quotedProductLength)))
    : 0;
  const otherCostsValue = hasNumericValue(otherCosts) ? Number(otherCosts) : 0;
  const laborCostValue = hasNumericValue(laborCost) ? Number(laborCost) : 0;

  const totalCost = cableCostValue + otherCostsValue + laborCostValue;
  return totalCost > 0 ? totalCost : null;
};

export const validateCustomers = (customers: any) => {
  if (!customers.length) return [];
  return customers.filter((customer: any) => customer.active && customer.name.trim().length && customer.name !== 'false');
};

export function translateOdooSalesOrderStatus(status: OdooStatus): string {
  const statusMap = {
    cancel: ORDERSTATUS.CANCELLED,
    done: ORDERSTATUS.ORDER_PLACED,
    draft: ORDERSTATUS.QUOTATION,
    draft_quote: ORDERSTATUS.QUOTATION,
    inv: ORDERSTATUS.ORDER_PLACED,
    lost: ORDERSTATUS.LOST,
    partial: ORDERSTATUS.ORDER_PLACED,
    pi: ORDERSTATUS.ORDER_PLACED,
    sale: ORDERSTATUS.ORDER_PLACED,
    sent: ORDERSTATUS.QUOTATION_SENT,
    shipped: ORDERSTATUS.ORDER_PLACED,
  };
  return statusMap[status];
}
/* =====================================================================================================================
 * ================================================== Interfaces =======================================================
 * =====================================================================================================================
 */
interface ColDefForDataGrid extends DynamicDataObject {
  headerName: string;
  field: string;
}

export interface VuetifyDataTableHeader {
  key: string;
  title: string;
  colspan?: number;
  rowspan?: number;
  fixed?: boolean;
  align?: 'start' | 'center' | 'end';
  width?: number;
  minWidth?: string;
  maxWidth?: string;
  sortable?: boolean;
  cellClass?: string | string[];
  formatter?: string;
  fallbackColumn?: string;
  fallbackTooltip?: string;
}

interface SaleOrderRowForDataGrid {
  id: string;
  orderId: string;
  orderName: string;
  customer: string;
  createDate: string;
  csm: string;
  pricingStatus: string;
  pricedByPriceList: boolean;
  health: number | null;
  priceHealth: PriceHealth | null;
  erpPricingStatus: string | null;
}
interface FormattedSaleOrdersDataForDataGrid {
  columnsDefs: ColDefForDataGrid[];
  rowsData: SaleOrderRowForDataGrid[];
}

export interface SaleLineDetailsForDataTable {
  partNumber: string;
  partNumberBase: string;
  isHigherLengthThanEverSold: boolean;
  familyCode: string;
  uomQty: string;
  pricingType: string;
  enginePriceType: string;
  totalCableCost: string | null;
  otherCosts: string;
  labor: string;
  materialMargin: number | null;
  productMargin: number | null;
  perFootAdder: string;
  suggestedPrice: string;
  enginePrice: string | null;
  enginePriceTooltip: string | null;
  odooPrice: string;
  isPriced: boolean;
  odooCost: string;
  totalCost: number | null;
  notes: string;
  customerId: string;
  id: string;
  erpId: string;
  quotedProductLength: number | null;
  quotedProductLengthUnits: string | null;
  basePrice: number | null;
  perMeterAdder: number | null;
  priceListId: string | null;
  priceListName: string | null;
  multiplePriceListIds: string[];
  multiplePriceListNames: string[];
  multiplePriceLists: Array<{
    priceListId: string;
    priceListName: string;
    price: number;
  }>;
  pricingInfo: LinePricingInfoData;
}
type OdooStatus =
  | 'cancel'
  | 'done'
  | 'draft'
  | 'draft_quote'
  | 'inv'
  | 'lost'
  | 'partial'
  | 'pi'
  | 'sale'
  | 'sent'
  | 'shipped';

// Define const for the status in pricing
export const ORDERSTATUS = {
  LOST: 'Lost',
  CANCELLED: 'Cancelled',
  QUOTATION: 'Quotation',
  QUOTATION_SENT: 'Quotation Sent',
  ORDER_PLACED: 'Order placed',
};
